import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import type { ReadingGoalView } from '@/types/api';
import { DotGrid } from './DotGrid';
import { ProgressBar } from './ProgressBar';

/**
 * Screen 5 — daily progress tracker.
 *
 * Renders the dot grid, progress bar, "X / Y days" stats, and (when
 * complete) the celebratory "Done!" banner with a 700ms reveal delay
 * matching the spec.
 *
 * The banner is gated on `goal.status === 'completed'` rather than a
 * derived `completedDays.length === totalDays` so a refresh after
 * completion still shows the banner — the server is the source of truth
 * for terminal state.
 */
export interface ProgressScreenProps {
  goal: ReadingGoalView;
  onMarkDay: (dayNumber: number) => void;
  onStartOver: () => void;
  onBackToBook: () => void;
}

export function ProgressScreen({
  goal,
  onMarkDay,
  onStartOver,
  onBackToBook,
}: ProgressScreenProps) {
  const isComplete = goal.status === 'completed';
  const [showBanner, setShowBanner] = useState(isComplete);

  // 700ms reveal delay when the goal flips to completed. Mounting with
  // an already-completed goal (e.g. coming back the next day) shows the
  // banner immediately.
  useEffect(() => {
    if (!isComplete) {
      setShowBanner(false);
      return;
    }
    if (showBanner) return;
    const t = window.setTimeout(() => setShowBanner(true), 700);
    return () => window.clearTimeout(t);
  }, [isComplete, showBanner]);

  const completedCount = goal.completedDays.length;
  const unitsRead = completedCount * goal.perDay;
  const unitsLeft = Math.max(0, goal.totalUnits - unitsRead);

  return (
    <section className="flex flex-col gap-4">
      <AnimatePresence initial={false}>
        {showBanner ? (
          <motion.div
            key="done-banner"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            role="status"
            aria-live="polite"
            className="rounded-3xl bg-gradient-to-br from-or-green-700 to-or-green-700/80 px-6 py-8 text-center text-white shadow-md"
          >
            <h2 className="font-display text-[28px] font-bold">🎉 You Did It!</h2>
            <p className="mt-1 text-[14px] opacity-90">
              You finished <strong>{goal.book.title}</strong> in {goal.totalDays} day
              {goal.totalDays === 1 ? '' : 's'}. Reading champion!
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <header className="text-center">
        <h2 className="font-display text-[24px] font-bold text-or-slate-900">
          📚 Book Progress!
        </h2>
        <p className="mt-1 text-[13px] text-or-slate-500">
          {goal.goalType.charAt(0).toUpperCase() + goal.goalType.slice(1)} ·{' '}
          {goal.perDay} per day · {goal.totalDays} days total
        </p>
      </header>

      <div className="rounded-3xl border border-or-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-bold text-or-slate-700">Overall Progress</span>
          <span className="text-[13px] font-bold text-or-green-700 tabular-nums">
            {completedCount} / {goal.totalDays} days
          </span>
        </div>
        <div className="mt-3">
          <ProgressBar completed={completedCount} total={goal.totalDays} />
        </div>
        <div className="mt-3 flex items-center justify-between text-[12px] font-medium text-or-slate-500">
          <span>
            ✅ {unitsRead} {goal.goalType} read
          </span>
          <span>
            📖 {unitsLeft} {goal.goalType} to go
          </span>
        </div>

        <hr className="my-5 border-t-2 border-dashed border-or-blue-100" />

        <p className="text-center text-[13px] font-bold text-or-slate-700">
          {isComplete
            ? 'Every day is checked off — incredible work! 🏆'
            : "Tap a button when you finish that day's reading! 🎯"}
        </p>

        <div className="mt-4">
          <DotGrid
            totalDays={goal.totalDays}
            completedDays={goal.completedDays}
            onMarkDay={onMarkDay}
            locked={isComplete}
          />
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between">
        <Button type="button" variant="secondary" size="md" onClick={onBackToBook}>
          ← Back to library
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onStartOver}>
          Start over
        </Button>
      </div>
    </section>
  );
}
